import ImageDropUpload from '../components/ImageDropUpload';
import config from '../config.json';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 연구 주제 수정 페이지
function EditResearch() {
  const navigate = useNavigate();
  const { id } = useParams();
  const imageUploadRef = useRef();
  const [formData, setFormData] = useState({
    title: '',
    image_link: '',
    display_order: 0
  });
  const [bullets, setBullets] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // 기존 데이터 로드
  useEffect(() => {
    fetchResearch();
  }, [id]);

  const fetchResearch = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/researches/${id}`);
      const data = await response.json();

      if (data.success) {
        setFormData({
          title: data.data.title || '',
          image_link: data.data.image_link || '',
          display_order: data.data.display_order || 0
        });
        
        // Bullets 데이터 로드
        if (data.data.bullets && data.data.bullets.length > 0) {
          setBullets(data.data.bullets.map(b => b.bullet_text));
        }
      } else {
        setError('Failed to load research data');
      }
    } catch (err) {
      console.error('Error fetching research:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Bullet point 변경
  const handleBulletChange = (index, value) => {
    const newBullets = [...bullets];
    newBullets[index] = value;
    setBullets(newBullets);
  };

  // Bullet point 추가
  const addBullet = () => {
    setBullets([...bullets, '']);
  };

  // Bullet point 삭제
  const removeBullet = (index) => {
    const newBullets = bullets.filter((_, i) => i !== index);
    setBullets(newBullets.length > 0 ? newBullets : ['']);
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      setError('Title is required');
      return;
    }

    // 이미지 업로드 (선택된 파일이 있을 때만)
    const uploadedLink = await imageUploadRef.current?.upload();
    if (uploadedLink === false) return;
    const finalFormData = uploadedLink
      ? { ...formData, image_link: uploadedLink }
      : formData;

    try {
      setSaving(true);
      setError(null);

      // 빈 bullet 제거
      const filteredBullets = bullets.filter(b => b.trim() !== '');

      const response = await fetch(`${API_URL}/researches/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...finalFormData,
          bullets: filteredBullets
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/researches');
        }, 1500);
      } else {
        setError(data.error || 'Failed to update research');
      }
    } catch (err) {
      console.error('Error updating research:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 삭제 처리
  const handleDelete = async () => {
    if (!window.confirm('정말로 이 연구를 삭제하시겠습니까?\n\n삭제된 데이터는 복구할 수 없습니다.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/researches/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        alert('삭제되었습니다.');
        navigate('/researches');
      } else {
        setError(data.error || 'Failed to delete research');
      }
    } catch (err) {
      console.error('Error deleting research:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading research data...</p>
      </div>
    );
  }

  // 성공 메시지
  if (success) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <h2 style={{ color: 'green' }}>Researches 페이지가 성공적으로 갱신되었습니다.</h2>
        <p>Researches 페이지로 돌아가는 중...</p>
      </div>
    );
  }

  return (
    <div className="site-content">
      <h2 className="section-title">Edit Research Topic</h2>

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            연구 제목 <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Image Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            이미지
          </label>
          <ImageDropUpload
            ref={imageUploadRef}
            uploadDir="research"
            imageBaseUrl={`${config.images.serverUrl}${config.images.basePath}`}
            currentImageLink={formData.image_link}
          />
        </div>

        {/* Bullet Points */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            설명
          </label>
          <small style={{ color: '#666' }}>HTML 태그를 지원합니다.</small>
          {bullets.map((bullet, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                value={bullet}
                onChange={(e) => handleBulletChange(index, e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              {bullets.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBullet(index)}
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addBullet}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            글머리 기호 추가
          </button>
        </div>

        {/* Display Order */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            표시 순서
          </label>
          <small style={{ color: '#666' }}>낮은 숫자가 먼저 표시됩니다. (0: 가장 높은 우선순위)</small>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            min="0"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Delete Button */}
        <div style={{ marginBottom: '30px' }}>
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            제거
          </button>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/researches')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#ddd',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Updating...' : '변경 사항 저장'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditResearch;
